"use client";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import type {} from "@airollplay/database";
import { z } from "@airollplay/zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { Send } from "lucide-react";
import Link from "next/link";
import { useRouter, useSearchParams } from "next/navigation";
import { useCallback, useState } from "react";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { sendPasswordResetRequest } from "./_actions/send-password-reset-request";

const schema = z.object({
  email: z.string().email(),
});

type FieldInputs = z.input<typeof schema>;
type FieldOutputs = z.output<typeof schema>;

export function PasswordResetRequestSendForm() {
  const router = useRouter();

  const searchParams = useSearchParams();

  const [isBusy, setIsBusy] = useState(false);

  const form = useForm<FieldInputs, unknown, FieldOutputs>({
    resolver: zodResolver(schema),
    defaultValues: {
      email: searchParams.get("email") || "",
    },
  });

  const submit = useCallback(
    async (values: FieldOutputs) => {
      try {
        setIsBusy(true);

        await toast.promise(sendPasswordResetRequest(values.email), {
          loading: "パスワード再設定リンクを送信しています...",
          success: "パスワード再設定リンクの送信に成功しました",
          error: "パスワード再設定リンクの送信に失敗しました",
        });

        router.refresh();
      } finally {
        setIsBusy(false);
      }
    },
    [router],
  );

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(submit)}
        className="flex flex-col gap-4">
        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem>
              <FormLabel>メールアドレス</FormLabel>
              <FormControl>
                <Input
                  type="email"
                  autoComplete="username"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <hr />

        <Button disabled={isBusy}>
          <div className="flex items-center gap-1">
            <Send size={16} />

            <span>パスワード再設定リンクを送信する</span>
          </div>
        </Button>

        <Button
          asChild
          variant="link">
          <Link
            href={[
              "/sign-in",
              `?${new URLSearchParams({
                ...(searchParams.has("a")
                  ? { a: searchParams.get("a") || "" }
                  : {}),
              }).toString()}`,
            ].join("")}>
            ログイン画面へ戻る
          </Link>
        </Button>
      </form>
    </Form>
  );
}
